import moment from 'moment-timezone'
import { PMBDateRangePicker } from 'components/PMBNewDatePicker'
import PMBPagination from 'components/PMBPagination'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import { TableComponent as DataTable } from 'components/TableComponent'
import { useDebounce } from 'helpers'
import { useState } from 'react'
import { ChevronDown } from 'react-feather'
import { Card, CardBody, Input, Spinner } from 'reactstrap'
import { formatDatePMBV2 } from 'utils/formatDate'
import { newFormatThousandSeparator } from 'utils/formatNumber'

const TabWorkRecords = ({
  items = [],
  totalItems = 0,
  isLoading = false,
  batchData,
  currentPage = 0,
  rowsPerPage = 10,
  setCurrentPage,
  setRowsPerPage,
  search = '',
  setSearch,
  sortColumn = 'createdAt',
  setSortColumn,
  sortDirection = 'DESC',
  setSortDirection,
  from = '',
  setFrom,
  to = '',
  setTo,
  refetch,
  selectedRows = [],
  onSelectedRowsChange,
}) => {
  const [searchInput, setSearchInput] = useState(search)

  const debouncedSearch = useDebounce((value) => {
    if (setCurrentPage) setCurrentPage(0)
    if (setSearch) setSearch(value)
    if (refetch) refetch()
  }, 600)

  const onHandleSearchChange = (value) => {
    setSearchInput(value ?? '')
    debouncedSearch(value ?? '')
  }

  const handleSort = (column, direction) => {
    if (column?.sortField) {
      if (setSortColumn) setSortColumn(column.sortField)
      if (setSortDirection)
        setSortDirection(direction?.toUpperCase() === 'ASC' ? 'ASC' : 'DESC')
      if (setCurrentPage) setCurrentPage(0)
    }
  }

  const handleSelectedRowsChange = (state) => {
    if (onSelectedRowsChange) {
      onSelectedRowsChange(state.selectedRows || [])
    }
  }

  const columns = [
    {
      id: 'createdAt',
      name: <span>Tanggal</span>,
      selector: (row) => row?.createdAt,
      sortable: true,
      sortField: 'createdAt',
      cell: (row) =>
        row?.createdAt ? formatDatePMBV2(row.createdAt) : row?.tanggal || '-',
    },
    {
      id: 'employeeName',
      name: <span>Nama Karyawan</span>,
      selector: (row) => row?.employeeName,
      sortable: true,
      sortField: 'employeeName',
      cell: (row) =>
        row?.employeeName ||
        (row?.employeeUserId ? `User #${row.employeeUserId}` : row?.nama || '-'),
    },
    {
      id: 'notes',
      name: <span>Note</span>,
      selector: (row) => row?.notes || row?.note,
      cell: (row) => row?.notes || row?.note || '-',
    },
    {
      id: 'amount',
      name: <span>Jumlah</span>,
      selector: (row) => row?.amount,
      sortable: true,
      sortField: 'amount',
      right: true,
      cell: (row) =>
        row?.amount !== undefined && row?.amount !== null
          ? `Rp${newFormatThousandSeparator(row.amount, 0)}`
          : '-',
    },
  ]

  return (
    <Card className="mb-3 border-0 shadow-sm">
      <CardBody>
        <DataTable
          columns={columns}
          data={items || []}
          progressPending={isLoading}
          responsive
          persistTableHead
          defaultSortFieldId={sortColumn || 'createdAt'}
          defaultSortAsc={sortDirection === 'ASC'}
          paginationTotalRows={totalItems}
          progressComponent={
            <div style={{ margin: '100px 0px' }}>
              <Spinner color="primary" />
            </div>
          }
          sortIcon={<ChevronDown size={14} />}
          sortServer
          onSort={handleSort}
          className="border p-0 border-1 rounded-top"
          noDataComponent={
            <p className="my-4 fw-semibold text-muted text-center">
              {!isLoading && 'Belum ada catatan kerja yang didaftarkan'}
            </p>
          }
          pagination
          paginationServer
          paginationComponent={(props) => (
            <PMBPagination
              rowCount={totalItems}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              setCurrentPage={setCurrentPage}
              setRowsPerPage={setRowsPerPage}
              props={props}
            />
          )}
          subHeader={true}
          subHeaderComponent={
            <div className="w-100">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                {/* Search Input */}
                <div style={{ minWidth: '240px', flex: 1, maxWidth: '350px' }}>
                  <PMBSearchInputGroup
                    value={searchInput}
                    placeholder="Cari nama karyawan atau note..."
                    onChange={(e) => onHandleSearchChange(e.target.value)}
                  />
                </div>

                <div style={{ minWidth: '240px' }}>
                  <PMBDateRangePicker
                    value={[from || null, to || null]}
                    onChange={(dates) => {
                      if (dates && dates.length === 2) {
                        if (setCurrentPage) setCurrentPage(0)
                        if (setFrom) setFrom(moment(dates[0]).format('YYYY-MM-DD'))
                        if (setTo) setTo(moment(dates[1]).format('YYYY-MM-DD'))
                      }
                    }}
                    onClear={() => {
                      if (setCurrentPage) setCurrentPage(0)
                      if (setFrom) setFrom('')
                      if (setTo) setTo('')
                    }}
                  />
                </div>
              </div>
            </div>
          }
        />
      </CardBody>
    </Card>
  )
}

export default TabWorkRecords
